/** Шапка: где мы находимся.
 *
 * Крошки тихо сверху, крупный заголовок ниже — и круглая кнопка «назад» ровно
 * в одной строке с заголовком. Раньше она стояла между крошками и названием и
 * терялась: возврат — частое действие, его место должно быть предсказуемым.
 *
 * На телефоне эта шапка съедала пятую часть экрана ради ответа на вопрос «где
 * я», который нужен в момент прихода, а не пока разглядываешь товар. Поэтому
 * она просто уезжает вверх вместе со страницей, как обычное содержимое, а
 * сверху остаётся узкая полоса с названием и возвратом.
 *
 * Уезжает, а не складывается, и это главное. Складывающаяся шапка меняет
 * высоту документа; браузер на изменившуюся высоту отвечает подкруткой, а
 * подкрутка снова дёргает шапку — страница мелко прыгает под пальцем, а у
 * нижнего края начинает частить. Здесь раскладка не меняется никогда: полоса
 * лежит поверх страницы и только проявляется. Меняется одна прозрачность, и
 * обратной связи в этом нет.
 *
 * Порог, на котором полоса проявляется, считается один раз на экран — по
 * высоте крупной шапки. Раньше сравнение с порогом и было источником дрожи, но
 * дрожало не оно: дрожала высота, которую порог менял. Теперь высота
 * постоянная, и порог стал просто порогом.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Screen } from './nav';
import { tap } from '@/shared/tg';

/** Высота узкой полосы: под ней метка и считается ушедшей. */
const MINI = 46;

function shortTitle(title: string): string {
  const line = title.split('\n')[0]?.trim() ?? '';
  return line.length > 20 ? `${line.slice(0, 20).trimEnd()}…` : line;
}

interface Props {
  stack: Screen[];
  title: string;
  onBack: () => void;
  onCrumb: (depth: number) => void;
}

function BackButton({ onBack, small }: { onBack: () => void; small?: boolean }) {
  return (
    <button
      type="button"
      aria-label="Назад"
      onClick={() => {
        tap();
        onBack();
      }}
      className={[
        'glass grid shrink-0 place-items-center rounded-full text-mint',
        'transition-transform duration-150 ease-glass active:scale-95 active:bg-glass-active',
        small ? 'size-8' : 'size-10',
      ].join(' ')}
    >
      <svg
        viewBox="0 0 24 24"
        width={small ? 18 : 22}
        height={small ? 18 : 22}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M15 6l-6 6 6 6" />
      </svg>
    </button>
  );
}

export function Topbar({ stack, title, onBack, onCrumb }: Props) {
  const ancestors = stack.slice(0, -1);
  const [passed, setPassed] = useState(false);
  const [gate, setGate] = useState(0);
  const head = useRef<HTMLDivElement>(null);

  // Высоту меряем один раз на экран, а не на каждое движение: у шапки с
  // крошками и без них она разная, но внутри экрана постоянная
  useLayoutEffect(() => {
    const node = head.current;
    setGate(node ? Math.max(0, node.offsetHeight - MINI) : 0);
  }, [stack.length, title]);

  useEffect(() => {
    const look = () => {
      const y = window.scrollY;
      // Полоска нечувствительности вокруг порога: страница под пальцем качается
      // на пару пикселей, и без неё это читалось бы миганием
      setPassed((was) => (was ? y > gate - 6 : y > gate + 6));
    };
    look();
    window.addEventListener('scroll', look, { passive: true });
    return () => window.removeEventListener('scroll', look);
  }, [gate]);

  return (
    <>
      {/* Узкая полоса поверх страницы. Ничего не занимает в раскладке: пока
          крупная шапка на виду, полоса прозрачна и не ловит нажатия */}
      <div className={passed ? 'topbar-mini on' : 'topbar-mini'}>
        {stack.length > 1 && <BackButton onBack={onBack} small />}
        <span className="min-w-0 truncate text-[16px] font-[680] tracking-[-.01em]">
          {title}
        </span>
      </div>

      <div ref={head} className="topbar-large">
        {ancestors.length > 0 && (
          <div className="mb-1.5 flex items-center gap-1.5 overflow-x-auto whitespace-nowrap [scrollbar-width:none]">
            {ancestors.map((screen, depth) => (
              <span key={depth} className="flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    tap();
                    onCrumb(depth);
                  }}
                  className="glass rounded-full px-2.5 py-1 text-xs text-link transition-[background-color,transform] duration-150 ease-glass active:scale-95 active:bg-glass-active"
                >
                  {shortTitle(screen.title)}
                </button>
                <span className="text-hint opacity-40">›</span>
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2.5">
          {stack.length > 1 && <BackButton onBack={onBack} />}
          <h1 className="min-w-0 truncate text-[22px] font-[680] tracking-[-.02em]">
            {title}
          </h1>
        </div>
      </div>
    </>
  );
}
